import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Plus, Edit, Trash2, Eye, CheckCircle, Users, Calendar,
  ChevronDown, ChevronUp, Search, Download, Loader2, Shield, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { legalApi } from '../../lib/api';
import { toast } from 'sonner';

const LegalAgreementsManager = () => {
  const [activeView, setActiveView] = useState('agreements'); // agreements, signatures, editor
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [expandedSignature, setExpandedSignature] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor state
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [agreementForm, setAgreementForm] = useState({
    name: '',
    slug: '',
    content: '',
    content_es: '',
    require_signature: true,
    require_scroll: true,
    checkboxes: []
  });
  const [newCheckbox, setNewCheckbox] = useState({ label: '', label_es: '', required: true });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [agreementsRes, signaturesRes] = await Promise.all([
        legalApi.getAgreements(),
        legalApi.getSignatures()
      ]);
      setAgreements(agreementsRes.data);
      setSignatures(signaturesRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load legal agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefault = async () => {
    try {
      const res = await legalApi.seedDefault();
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to seed default agreement');
    }
  };

  const handleActivate = async (agreementId) => {
    try {
      await legalApi.activateAgreement(agreementId);
      toast.success('Agreement activated');
      loadData();
    } catch (err) {
      toast.error('Failed to activate agreement');
    }
  };

  const handleDelete = async (agreementId) => {
    if (!window.confirm('Are you sure you want to delete this agreement?')) return;
    try {
      await legalApi.deleteAgreement(agreementId);
      toast.success('Agreement deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete agreement');
    }
  };

  const handleEditAgreement = (agreement) => {
    setEditingAgreement(agreement);
    setAgreementForm({
      name: agreement.name,
      slug: agreement.slug,
      content: agreement.content,
      content_es: agreement.content_es || '',
      require_signature: agreement.require_signature,
      require_scroll: agreement.require_scroll,
      checkboxes: agreement.checkboxes || []
    });
    setActiveView('editor');
  };

  const handleNewAgreement = () => {
    setEditingAgreement(null);
    setAgreementForm({
      name: '',
      slug: '',
      content: '',
      content_es: '',
      require_signature: true,
      require_scroll: true,
      checkboxes: []
    });
    setActiveView('editor');
  };

  const handleSaveAgreement = async () => {
    if (!agreementForm.name || !agreementForm.slug || !agreementForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingAgreement) {
        await legalApi.updateAgreement(editingAgreement.id, agreementForm);
        toast.success('Agreement updated');
      } else {
        await legalApi.createAgreement(agreementForm);
        toast.success('Agreement created');
      }
      setActiveView('agreements');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save agreement');
    }
  };

  const addCheckbox = () => {
    if (!newCheckbox.label) return;
    const checkbox = {
      id: `checkbox_${Date.now()}`,
      label: newCheckbox.label,
      label_es: newCheckbox.label_es,
      required: newCheckbox.required
    };
    setAgreementForm(prev => ({
      ...prev,
      checkboxes: [...prev.checkboxes, checkbox]
    }));
    setNewCheckbox({ label: '', label_es: '', required: true });
  };

  const removeCheckbox = (checkboxId) => {
    setAgreementForm(prev => ({
      ...prev,
      checkboxes: prev.checkboxes.filter(cb => cb.id !== checkboxId)
    }));
  };

  const filteredSignatures = signatures.filter(sig => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      sig.signer_name?.toLowerCase().includes(term) ||
      sig.signer_email?.toLowerCase().includes(term) ||
      sig.product_name?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="legal-agreements-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Legal Agreements</h2>
          <p className="text-sm text-[#555555]">Manage pre-purchase legal agreements and view signatures</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeView === 'agreements' ? 'default' : 'outline'}
            onClick={() => setActiveView('agreements')}
            className={activeView === 'agreements' ? 'bg-[#1F2A44]' : ''}
          >
            <FileText className="w-4 h-4 mr-2" />
            Agreements
          </Button>
          <Button
            variant={activeView === 'signatures' ? 'default' : 'outline'}
            onClick={() => setActiveView('signatures')}
            className={activeView === 'signatures' ? 'bg-[#1F2A44]' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Signatures ({signatures.length})
          </Button>
        </div>
      </div>

      {/* Agreements List View */}
      {activeView === 'agreements' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button onClick={handleNewAgreement} className="bg-[#1F2A44]">
                <Plus className="w-4 h-4 mr-2" />
                New Agreement
              </Button>
              {agreements.length === 0 && (
                <Button variant="outline" onClick={handleSeedDefault}>
                  <Shield className="w-4 h-4 mr-2" />
                  Seed Default Agreement
                </Button>
              )}
            </div>
          </div>

          {agreements.length === 0 ? (
            <div className="bg-white border border-[#E8E4DC] rounded p-12 text-center">
              <FileText className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#222222] mb-2">No Legal Agreements</h3>
              <p className="text-[#555555] mb-4">Create your first legal agreement or seed the default one.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {agreements.map((agreement) => (
                <div key={agreement.id} className="bg-white border border-[#E8E4DC] rounded p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-[#222222]">{agreement.name}</h3>
                        <Badge className={agreement.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {agreement.status}
                        </Badge>
                        <Badge variant="outline">v{agreement.version}</Badge>
                      </div>
                      <p className="text-sm text-[#555555] mb-3">Slug: /{agreement.slug}</p>
                      <div className="flex gap-4 text-sm text-[#555555]">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {agreement.checkboxes?.length || 0} checkboxes
                        </span>
                        <span>
                          {agreement.require_signature ? '✓ Signature required' : '✗ No signature'}
                        </span>
                        <span>
                          {agreement.require_scroll ? '✓ Scroll required' : '✗ No scroll'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {agreement.status !== 'active' && (
                        <Button variant="outline" size="sm" onClick={() => handleActivate(agreement.id)}>
                          Activate
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleEditAgreement(agreement)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(agreement.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Signatures View */}
      {activeView === 'signatures' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555555]" />
              <Input
                placeholder="Search by name, email, or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <span className="text-sm text-[#555555]">{filteredSignatures.length} signatures</span>
          </div>

          {filteredSignatures.length === 0 ? (
            <div className="bg-white border border-[#E8E4DC] rounded p-12 text-center">
              <Users className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#222222] mb-2">No Signatures Yet</h3>
              <p className="text-[#555555]">Signatures will appear here when users sign agreements.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSignatures.map((sig) => (
                <div key={sig.id} className="bg-white border border-[#E8E4DC] rounded overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#FDFCFA]"
                    onClick={() => setExpandedSignature(expandedSignature === sig.id ? null : sig.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#1F2A44] rounded-full flex items-center justify-center text-white font-semibold">
                        {sig.signer_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-[#222222]">{sig.signer_name}</p>
                        <p className="text-sm text-[#555555]">{sig.signer_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-[#222222]">{sig.agreement_name}</p>
                        <p className="text-xs text-[#555555]">{formatDate(sig.signed_at)}</p>
                      </div>
                      {expandedSignature === sig.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {expandedSignature === sig.id && (
                    <div className="border-t border-[#E8E4DC] p-4 bg-[#FDFCFA]">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-[#555555]">Agreement Version</p>
                          <p className="font-medium">{sig.agreement_version}</p>
                        </div>
                        <div>
                          <p className="text-[#555555]">Phone</p>
                          <p className="font-medium">{sig.signer_phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[#555555]">Product</p>
                          <p className="font-medium">{sig.product_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[#555555]">Scrolled to Bottom</p>
                          <p className="font-medium">{sig.scrolled_to_bottom ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <p className="text-[#555555]">Checkboxes Checked</p>
                          <p className="font-medium">{sig.checkboxes_checked?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-[#555555]">IP Hash</p>
                          <p className="font-medium font-mono text-xs">{sig.ip_address_hash || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Agreement Editor View */}
      {activeView === 'editor' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#222222]">
              {editingAgreement ? 'Edit Agreement' : 'New Agreement'}
            </h3>
            <Button variant="outline" onClick={() => setActiveView('agreements')}>
              Cancel
            </Button>
          </div>

          <div className="bg-white border border-[#E8E4DC] rounded p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Agreement Name *</Label>
                <Input
                  id="name"
                  value={agreementForm.name}
                  onChange={(e) => setAgreementForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Service Agreement"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={agreementForm.slug}
                  onChange={(e) => setAgreementForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  placeholder="e.g., service-agreement"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Requirements */}
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={agreementForm.require_scroll}
                  onCheckedChange={(checked) => setAgreementForm(prev => ({ ...prev, require_scroll: checked }))}
                />
                <Label>Require scroll to bottom</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={agreementForm.require_signature}
                  onCheckedChange={(checked) => setAgreementForm(prev => ({ ...prev, require_signature: checked }))}
                />
                <Label>Require e-signature</Label>
              </div>
            </div>

            {/* Content */}
            <div>
              <Label htmlFor="content">Agreement Content (English) * — Supports Markdown</Label>
              <Textarea
                id="content"
                value={agreementForm.content}
                onChange={(e) => setAgreementForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter the legal agreement text in Markdown format..."
                className="mt-1 font-mono text-sm"
                rows={12}
              />
            </div>

            <div>
              <Label htmlFor="content_es">Agreement Content (Spanish) — Supports Markdown</Label>
              <Textarea
                id="content_es"
                value={agreementForm.content_es}
                onChange={(e) => setAgreementForm(prev => ({ ...prev, content_es: e.target.value }))}
                placeholder="Ingrese el texto del acuerdo legal en formato Markdown..."
                className="mt-1 font-mono text-sm"
                rows={8}
              />
            </div>

            {/* Checkboxes */}
            <div>
              <Label className="mb-3 block">Required Acknowledgment Checkboxes</Label>
              
              {agreementForm.checkboxes.length > 0 && (
                <div className="space-y-2 mb-4">
                  {agreementForm.checkboxes.map((cb, idx) => (
                    <div key={cb.id} className="flex items-center justify-between p-3 bg-[#FDFCFA] border border-[#E8E4DC] rounded">
                      <div>
                        <p className="font-medium text-sm">{cb.label}</p>
                        {cb.label_es && <p className="text-xs text-[#555555]">ES: {cb.label_es}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={cb.required ? 'default' : 'outline'}>
                          {cb.required ? 'Required' : 'Optional'}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => removeCheckbox(cb.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-4 border border-dashed border-[#E8E4DC] rounded">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <Input
                    placeholder="Checkbox label (English)"
                    value={newCheckbox.label}
                    onChange={(e) => setNewCheckbox(prev => ({ ...prev, label: e.target.value }))}
                  />
                  <Input
                    placeholder="Checkbox label (Spanish)"
                    value={newCheckbox.label_es}
                    onChange={(e) => setNewCheckbox(prev => ({ ...prev, label_es: e.target.value }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCheckbox.required}
                      onCheckedChange={(checked) => setNewCheckbox(prev => ({ ...prev, required: checked }))}
                    />
                    <Label>Required</Label>
                  </div>
                  <Button variant="outline" size="sm" onClick={addCheckbox} disabled={!newCheckbox.label}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Checkbox
                  </Button>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setActiveView('agreements')}>
                Cancel
              </Button>
              <Button onClick={handleSaveAgreement} className="bg-[#1F2A44]">
                <CheckCircle className="w-4 h-4 mr-2" />
                {editingAgreement ? 'Update Agreement' : 'Create Agreement'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalAgreementsManager;
